/**
 * Watch PDP canonical URLs derived from the designer-choice attribute.
 *
 * Pure and network-free: the caller resolves the designer choice and passes it
 * in.
 */
import { buildWatchUrl } from '@/lib/product/urls';

import { buildCanonicalUrl } from './canonical';

/** The segments a watch PDP URL is built from. */
export interface PdpCanonicalTarget {
  sku: string;
  collectionSlug: string;
  modelSlug: string;
}

export interface PdpCanonicalOptions {
  /** Locale prefix, e.g. `us-en`. */
  locale: string;
  product: PdpCanonicalTarget;
  /** `null` when the model has no designer choice. */
  designerChoice: PdpCanonicalTarget | null;
  baseUrl?: string;
}

/**
 * Absolute canonical URL for a watch PDP: the designer choice's PDP, or the
 * product's own when it is the designer choice or its model has none.
 *
 * `product` must carry Saleor's slugs rather than the request's path segments,
 * so the self-referencing case emits the canonical form of the URL instead of
 * blessing a lowercase SKU or a stale collection segment.
 */
export function buildPdpCanonicalUrl({
  locale,
  product,
  designerChoice,
  baseUrl,
}: PdpCanonicalOptions): string {
  const target = designerChoice ?? product;
  const path = `/${locale}${buildWatchUrl(target.collectionSlug, target.modelSlug, target.sku)}`;

  return buildCanonicalUrl({ path, baseUrl });
}
